import React, { useState, useEffect, useCallback } from 'react';
import styles from '../../pages/dashboard/DashboardUI.module.css';
import settingsStyles from '../../pages/dashboard/Settings.module.css';
import * as labelService from '../../services/labelService';

const COLOR_PALETTE = [
  '#3b82f6', '#10b981', '#f59e0b', '#8b5cf6',
  '#ec4899', '#06b6d4', '#f97316', '#84cc16',
  '#6366f1', '#14b8a6', '#ef4444', '#6b7280'
];

const COLOR_NAMES = [
  'Blue', 'Emerald', 'Amber', 'Violet',
  'Pink', 'Cyan', 'Orange', 'Lime',
  'Indigo', 'Teal', 'Red', 'Gray'
];

function getTextColor(bgColor) {
  if (!bgColor) return '#ffffff';
  const hex = bgColor.replace('#', '');
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.5 ? '#1f2937' : '#ffffff';
}

export default function LabelManager({ onMessage }) {
  const [labels, setLabels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingLabel, setEditingLabel] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    color: '#3b82f6',
    description: ''
  });

  const loadLabels = useCallback(async () => {
    try {
      setLoading(true);
      const data = await labelService.getLabels();
      setLabels(data.labels || []);
    } catch (error) {
      onMessage({ message: error.message || 'Failed to load labels', type: 'error' });
    } finally {
      setLoading(false);
    }
  }, [onMessage]);

  useEffect(() => {
    loadLabels();
  }, [loadLabels]);

  const openCreateModal = () => {
    setEditingLabel(null);
    setFormData({ name: '', color: '#3b82f6', description: '' });
    setShowModal(true);
  };

  const openEditModal = (label) => {
    setEditingLabel(label);
    setFormData({
      name: label.name,
      color: label.color || '#6b7280',
      description: label.description || ''
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingLabel(null);
    setFormData({ name: '', color: '#3b82f6', description: '' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      onMessage({ message: 'Label name is required', type: 'error' });
      return;
    }

    try {
      if (editingLabel) {
        await labelService.updateLabel(editingLabel.label_id, {
          name: formData.name.trim(),
          color: formData.color,
          description: formData.description.trim()
        });
        onMessage({ message: 'Label updated successfully', type: 'success' });
      } else {
        await labelService.createLabel({
          name: formData.name.trim(),
          color: formData.color,
          description: formData.description.trim()
        });
        onMessage({ message: 'Label created successfully', type: 'success' });
      }
      closeModal();
      await loadLabels();
    } catch (error) {
      onMessage({ message: error.message || 'Failed to save label', type: 'error' });
    }
  };

  const handleDelete = async (label) => {
    if (deleting === label.label_id) return;
    setDeleting(label.label_id);
    try {
      await labelService.deleteLabel(label.label_id);
      onMessage({ message: `Label "${label.name}" deleted`, type: 'success' });
      await loadLabels();
    } catch (error) {
      onMessage({ message: error.message || 'Failed to delete label', type: 'error' });
    } finally {
      setDeleting(null);
    }
  };

  if (loading) {
    return <div className={settingsStyles.loading}>Loading labels...</div>;
  }

  return (
    <div>
      <div className={settingsStyles.sectionHeader}>
        <div>
          <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 600, color: '#1e293b' }}>
            Labels & Categories
          </h3>
          <p className={settingsStyles.cardDesc} style={{ margin: '4px 0 0 0' }}>
            Manage custom labels for projects, leads, and other items.
          </p>
        </div>
        <button className={styles.buttonPrimary} onClick={openCreateModal}>
          + New Label
        </button>
      </div>

      {labels.length === 0 ? (
        <div className={settingsStyles.emptyState}>
          <p>No labels yet. Create your first label to organize projects and leads.</p>
        </div>
      ) : (
        <div className={settingsStyles.labelsList}>
          {labels.map((label) => (
            <div key={label.label_id} className={settingsStyles.labelCard}>
              <div className={settingsStyles.labelInfo}>
                <span
                  className={settingsStyles.labelBadge}
                  style={{
                    backgroundColor: label.color || '#6b7280',
                    color: getTextColor(label.color)
                  }}
                >
                  {label.name}
                </span>
                {label.description && (
                  <span className={settingsStyles.labelDescription}>
                    {label.description}
                  </span>
                )}
              </div>
              <div className={settingsStyles.roleActions}>
                <button
                  className={styles.buttonSecondary}
                  onClick={() => openEditModal(label)}
                >
                  Edit
                </button>
                <button
                  className={styles.buttonDanger}
                  onClick={() => handleDelete(label)}
                  disabled={deleting === label.label_id}
                >
                  {deleting === label.label_id ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {showModal && (
        <div className={settingsStyles.modalOverlay} onClick={closeModal}>
          <div className={settingsStyles.modal} onClick={(e) => e.stopPropagation()} style={{ minHeight: 'auto' }}>
            <div className={settingsStyles.modalHeader}>
              <h3>{editingLabel ? 'Edit Label' : 'Create Label'}</h3>
              <button className={settingsStyles.closeButton} onClick={closeModal}>
                ×
              </button>
            </div>
            <div className={settingsStyles.modalContent}>
              <form onSubmit={handleSubmit}>
                <div className={settingsStyles.formGroup}>
                  <label>Name</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="e.g. Urgent, Design, Backend"
                    autoFocus
                  />
                </div>

                <div className={settingsStyles.formGroup}>
                  <label>Color</label>
                  <div className={settingsStyles.colorPicker}>
                    {COLOR_PALETTE.map((color, index) => (
                      <button
                        key={color}
                        type="button"
                        className={`${settingsStyles.colorSwatch} ${formData.color === color ? settingsStyles.colorSwatchActive : ''}`}
                        style={{ backgroundColor: color }}
                        onClick={() => setFormData({ ...formData, color })}
                        title={COLOR_NAMES[index]}
                        aria-label={COLOR_NAMES[index]}
                      />
                    ))}
                  </div>
                </div>

                <div className={settingsStyles.formGroup}>
                  <label>Preview</label>
                  <div>
                    <span
                      className={settingsStyles.labelBadge}
                      style={{
                        backgroundColor: formData.color,
                        color: getTextColor(formData.color)
                      }}
                    >
                      {formData.name || 'Label Preview'}
                    </span>
                  </div>
                </div>

                <div className={settingsStyles.formGroup}>
                  <label>Description (optional)</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="What is this label used for?"
                    rows={2}
                  />
                </div>

                <div className={settingsStyles.modalActions}>
                  <button className={styles.buttonSecondary} onClick={closeModal} type="button">
                    Cancel
                  </button>
                  <button className={styles.buttonPrimary} type="submit">
                    {editingLabel ? 'Save Changes' : 'Create Label'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
